import { applyContactDefaults, contactDestinations, contactUrl, defaultContactMethod, validContactUrl } from '@/helpers/contact';
import { checkPwaCompatibility } from '@/helpers/pwaCompat';
import { getAllAsDictionary, openIdb } from '@/idb';
import Message from '@/models/Message';
import MessageType from '@/models/MessageType';
import { defineStore } from 'pinia';

// Determine environment based on hostname
const getEnvironment = () => {
	const hostname = self.location.hostname;
	const LIVE_DOMAIN = 'kiosk.safandarley.com';
	let env = 'staging';

	if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
		env = 'development';
	} else if (hostname === LIVE_DOMAIN) {
		env = 'production';
	}

	return env;
};

const state = () => ({
	env: getEnvironment(), // 'production' | 'staging' | 'development'
	debugging: process.env.NODE_ENV !== 'production' || self.location.hostname === 'localhost' || /\.ngrok\.io$/.test(self.location.hostname),
	settings: {
		useJsKeyboard: false,
		inactivityReset: false,
		inactivityMinutes: 1,
		disableRightClick: false,
		disableUserSelect: false,
		metricUnits: actions.setDefaultEU(),
		hideUsRegions: actions.setDefaultEU(),
		language: 'en',
		contact: { method: defaultContactMethod(), destination: 'usa', customUrl: '' },
	},
	installPrompt: null,
	showInstallBanner: false,
	showInstallButton: false,
	serviceWorkerUpdate: false,
	videoPopupOpen: false,
	messages: [],
	swStatus: {
		registered: false,
		ready: false,
		contentCached: false,
		videoCache: null,
		appDownload: null,
		isOffline: false,
		updating: false,
		updateAvailable: false,
		error: false,
		errorMsg: null
	},
	// Centralized PWA compatibility snapshot
	pwaCompat: {
		verdict: 'checking', // 'checking' | 'full' | 'partial' | 'none'
		restrictions: {
			serviceWorker: { isBlocked: false, isSupported: false, blockReason: null },
			cacheAPI: { isBlocked: false, isSupported: false, blockReason: null },
			installation: { isBlocked: false, isSupported: false, blockReason: null }
		},
		hasServiceWorker: false,
		hasCacheAPI: false,
		canBeInstalled: false,
		isSecure: false,
		lastCheckedTs: 0
	}
});

const getters = {
	contactUrl: state => contactUrl(state.settings.contact),
};

const actions = {
	async initializeSettings() {
		const idb = await openIdb();
		await applyContactDefaults(idb);
		await this.refreshSettings();
	},
	async saveSetting(key, value) {
		const idb = await openIdb();
		await idb.put('settings', value, key);
		await this.refreshSettings();
		if ('BroadcastChannel' in window) {
			const broadcast = new BroadcastChannel('settings');
			broadcast.postMessage('update');
			broadcast.close();
		}
	},
	async refreshSettings() {
		const idb = await openIdb();
		const values = await getAllAsDictionary(idb, 'settings');
		this.settings.useJsKeyboard = values.useJsKeyboard ?? false;
		this.settings.inactivityReset = values.inactivityReset ?? false;
		this.settings.inactivityMinutes = Number.isInteger(values.inactivityMinutes) && values.inactivityMinutes >= 1 && values.inactivityMinutes <= 5 ? values.inactivityMinutes : 1;
		this.settings.disableRightClick = values.disableRightClick ?? false;
		this.settings.disableUserSelect = values.disableUserSelect ?? false;
		this.settings.metricUnits = values.metricUnits ?? actions.setDefaultEU();
		this.settings.hideUsRegions = values.hideUsRegions ?? this.setDefaultEU();
		this.settings.language = values.language ?? 'en';
		const contact = values.contact ?? {};
		const contactMethod = contact.method ?? defaultContactMethod();
		this.settings.contact = {
			method: contactMethod === 'qr' && contactUrl(contact) ? 'qr' : 'form',
			destination: contact.destination === 'custom' || Object.hasOwn(contactDestinations, contact.destination) ? contact.destination : 'usa',
			customUrl: validContactUrl(contact.customUrl),
		};
	},
	removeMessage(messageId) {
		const index = this.messages.findIndex(x => x.id === messageId);
		if (index >= 0) {
			this.messages.splice(index, 1);
		}
	},
	addMessage(messageText, type = MessageType.info, autoClose = true) {
		if (!(typeof messageText === 'string')) { return; }
		messageText = messageText.trim();
		if (!messageText) { return; }

		const currentId = this.messages.length > 0 ? this.messages.reduce((id, x) => Math.max(id, x.id), 0) : 0;
		const message = new Message();
		message.id = currentId + 1;
		message.type = MessageType.isValid(type) ? type : MessageType.info;
		message.message = messageText;
		message.autoClose = !!autoClose;

		if (this.messages.findIndex(x => x.id === message.id) >= 0) { return; }
		this.messages.push(message);
		if (autoClose) {
			setTimeout(() => {
				this.removeMessage(message.id);
				// 1s delay for animate out:
			}, Message.AUTO_CLOSE_DELAY + 1000);
		}
	},
	setCustomPwaBanner(status) {
		if (status && localStorage.getItem('pwaUserChoice') != 'no') {
			this.showInstallBanner = true;
		} else {
			this.showInstallBanner = false;
		}
	},
	setPwaPrompt(event) {
		if (event != null) {
			this.installPrompt = event;
		}
	},
	setDefaultEU() {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return timeZone.startsWith('Europe/');
	},
	async refreshPwaCompat() {
		try {
			const result = await checkPwaCompatibility();
			const full = result.hasServiceWorker && result.hasCacheAPI && result.canBeInstalled && result.isSecure;
			const verdict = full ? 'full' : ((result.hasServiceWorker && result.isSecure) ? 'partial' : 'none');
			this.pwaCompat.verdict = verdict;
			this.pwaCompat.restrictions = result.restrictions;
			this.pwaCompat.hasServiceWorker = result.hasServiceWorker;
			this.pwaCompat.hasCacheAPI = result.hasCacheAPI;
			this.pwaCompat.canBeInstalled = result.canBeInstalled;
			this.pwaCompat.isSecure = result.isSecure;
			this.pwaCompat.lastCheckedTs = Date.now();
		} catch (e) {
			this.pwaCompat.verdict = 'none';
			this.pwaCompat.lastCheckedTs = Date.now();
		}
	}
};

export const useStore = defineStore('general', {
	state,
	getters,
	actions,
});
